/**
 * Pedidos em quadro Kanban por ESTADO. Mesma máquina de estados da lista (mudar a etapa é feito no
 * detalhe do pedido); o quadro só agrupa. Cada cartão mostra número, canal, cliente, produto e total.
 * Com a carga real cada coluna pode ter centenas de pedidos: desenha os 30 mais recentes e diz quantos
 * há — a contagem e a soma do cabeçalho são do conjunto inteiro filtrado.
 */
import { createElement as h, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../ui/index.js";
import { KanbanBoard } from "../ui/index.js";
import { formatBrl, formatCount } from "../format.js";
import { formatDate } from "../finance/format.js";
import { salesChannelKey, salesChannelLabel } from "../orders/channels.js";
import { productSummary } from "../orders/products.js";
import { ORDER_STATES, ORDER_STATE_LABELS, isRevenueState, type PlatformOrderState } from "../orders/state-machine.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

export const KANBAN_CARDS_PER_COLUMN = 30;
const ALWAYS_SHOWN: readonly PlatformOrderState[] = ["payment_pending", "paid", "separation", "shipping", "invoiced", "delivered"];

export interface OrderKanbanColumn {
  state: PlatformOrderState;
  title: string;
  total: number;
  revenueCents: number;
  orders: PlatformUnifiedOrder[];
}

/** Colunas por estado: as seis da linha principal sempre; cancelado/devolvido só quando há pedido. */
export function orderKanbanColumns(orders: PlatformUnifiedOrder[]): OrderKanbanColumn[] {
  return ORDER_STATES.map((state) => {
    const rows = orders.filter((order) => order.state === state).sort((a, b) => b.placedAt.localeCompare(a.placedAt));
    return {
      state,
      title: ORDER_STATE_LABELS[state],
      total: rows.length,
      revenueCents: rows.reduce((sum, order) => (isRevenueState(order.state) ? sum + (order.totalCents ?? 0) : sum), 0),
      orders: rows
    };
  }).filter((column) => column.total > 0 || ALWAYS_SHOWN.includes(column.state));
}

function KanbanOrderCard({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  const product = productSummary(order);
  return h(
    "article",
    { className: "flex flex-col gap-1.5 rounded-control border border-border bg-surface p-3 text-sm", "data-record-id": order.id, "data-order-state": order.state },
    h("span", { className: "flex items-center justify-between gap-2" },
      h(Link, { to: `/pedidos/${order.id}`, className: "font-bold text-brand-text" }, `#${order.externalId}`),
      h(Badge, { tone: "neutral", title: order.channelName ?? undefined }, salesChannelLabel(salesChannelKey(order)))
    ),
    h("span", { className: "font-semibold text-ink" }, product.first),
    product.more > 0 ? h("span", { className: "text-xs text-muted" }, `+ ${product.more} outro(s) item(ns)`) : null,
    h("span", { className: "text-muted" }, order.customer?.name ?? "Cliente não informado"),
    h("span", { className: "flex items-center justify-between gap-2" },
      h("span", { className: "font-extrabold tabular-nums text-ink" }, order.totalCents === null ? "—" : formatBrl(order.totalCents)),
      h("span", { className: "text-xs text-muted" }, formatDate(order.placedAt))
    ),
    order.blingSituation ? h("span", { className: "text-xs text-muted" }, `Bling: ${order.blingSituation}`) : null
  );
}

export function OrderKanban({ orders }: { orders: PlatformUnifiedOrder[] }): ReactElement {
  const columns = orderKanbanColumns(orders);
  return h(KanbanBoard, {
    label: "Pedidos por estado",
    columns: columns.map((column) => ({
      id: column.state,
      title: `${column.title} (${formatCount(column.total)})`,
      hint: [column.revenueCents > 0 ? formatBrl(column.revenueCents) : null, column.total > KANBAN_CARDS_PER_COLUMN ? `${KANBAN_CARDS_PER_COLUMN} mais recentes` : null].filter(Boolean).join(" · ") || undefined,
      cards: column.orders.slice(0, KANBAN_CARDS_PER_COLUMN).map((order) => ({ id: order.id, node: h(KanbanOrderCard, { order }) }))
    }))
  });
}
